/**
 * Spicebombs Club Bundle Builder — JavaScript
 *
 * Responsibilities:
 *  1. Deal selection (2-pack / 3-pack)
 *  2. Quantity selector per flavor (duplicates allowed)
 *  3. Dynamic progress text, price summary, and ATC button copy
 *  4. Add-to-cart via Shopify Ajax API /cart/add.js
 *  5. Open the theme cart drawer/popover on success (respects themeVariables.settings.cartType)
 *
 * Cart line properties set per item:
 *   _bundle_source = spicebombs_club
 *   _bundle_deal   = club_2_pack | club_3_pack
 */

(function () {
  'use strict';

  // ── Tracking helper ────────────────────────────────────────────────────
  function trackEvent(eventName, payload) {
    if (window.dataLayer && Array.isArray(window.dataLayer)) {
      window.dataLayer.push(Object.assign({ event: eventName }, payload));
    }
  }

  // ── Klaviyo email helper ────────────────────────────────────────────────
  function getKlaviyoEmail() {
    try {
      var match = document.cookie.match(/(?:^|;\s*)__kla_id=([^;]+)/);
      if (!match) return '';
      var decoded = atob(decodeURIComponent(match[1]));
      var parsed = JSON.parse(decoded);
      return parsed['$email'] || '';
    } catch (e) {
      return '';
    }
  }

  // ── n8n webhook helper ──────────────────────────────────────────────────
  function sendClubWebhook(deal, selectedFlavors, bundlePrice) {
    try {
      fetch('https://spicebombs.app.n8n.cloud/webhook/spicebombs-club-cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({
          email:            getKlaviyoEmail(),
          club_deal:        deal,
          selected_flavors: selectedFlavors,
          bundle_price:     bundlePrice,
          timestamp:        new Date().toISOString(),
          page_url:         window.location.href
        })
      });
    } catch (e) {
      // Tracking failure never blocks add-to-cart
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    var section = document.querySelector('.abz-club-builder');
    if (!section) return;

    trackEvent('club_page_view', { page_type: 'spicebombs_club' });

    // ── Deal config (pricing for dynamic copy) ─────────────────────────
    var dealConfig = {
      club_2_pack: {
        normal:   '€49,98',
        club:     '€39,99',
        savings:  '€9,99',
        required: 2
      },
      club_3_pack: {
        normal:   '€74,97',
        club:     '€49,99',
        savings:  '€24,98',
        required: 3
      }
    };

    // ── State ──────────────────────────────────────────────────────────
    var selectedDeal          = null;
    var requiredQty           = 0;
    var quantities            = {}; // variantId (string) → count (int)
    var bundleCompleteTracked = false;

    // ── Element references ─────────────────────────────────────────────
    var dealCards        = section.querySelectorAll('.abz-deal-card');
    var dealHint         = section.querySelector('.abz-club-builder__deal-hint');
    var flavorsPanel     = section.querySelector('.abz-club-builder__flavors');
    var progressText     = section.querySelector('.abz-club-builder__progress-text');
    var progressBarWrap  = section.querySelector('.abz-club-builder__progress-bar-wrap');
    var progressBar      = section.querySelector('.abz-club-builder__progress-bar');
    var priceSummary     = section.querySelector('.abz-club-builder__price-summary');
    var priceNormalVal   = section.querySelector('.abz-price-normal-val');
    var priceClubVal     = section.querySelector('.abz-price-club-val');
    var priceSavingsVal  = section.querySelector('.abz-price-savings-val');
    var noDealMsg        = section.querySelector('.abz-club-builder__no-deal-msg');
    var flavorGrid       = section.querySelector('.abz-club-builder__grid');
    var atcBtn           = section.querySelector('.abz-club-builder__atc-btn');
    var flavorCards      = section.querySelectorAll('.abz-flavor-card');

    // Initialize quantities to 0
    flavorCards.forEach(function (card) {
      quantities[card.dataset.variant] = 0;
    });

    // ── Tracking: build selected-flavors snapshot ──────────────────────
    function getSelectedFlavors() {
      var flavors = [];
      flavorCards.forEach(function (card) {
        var qty = quantities[card.dataset.variant] || 0;
        if (qty > 0) {
          var nameEl = card.querySelector('.abz-flavor-card__name');
          flavors.push({
            flavor: nameEl ? nameEl.textContent.trim() : card.dataset.variant,
            quantity: qty
          });
        }
      });
      return flavors;
    }

    // ── Deal card selection ────────────────────────────────────────────
    function selectDeal(card) {
      var deal     = card.dataset.deal;
      var required = parseInt(card.dataset.required, 10);

      // Update deal card UI
      dealCards.forEach(function (c) {
        c.classList.remove('is-selected');
        c.setAttribute('aria-pressed', 'false');
      });
      card.classList.add('is-selected');
      card.setAttribute('aria-pressed', 'true');

      // Update state
      selectedDeal          = deal;
      requiredQty           = required;
      bundleCompleteTracked = false;

      trackEvent('club_deal_selected', { deal: deal });

      // Reset all quantities
      flavorCards.forEach(function (fc) {
        quantities[fc.dataset.variant] = 0;
      });

      // Unlock product grid and hide the pre-selection cues
      if (flavorGrid) flavorGrid.classList.remove('is-locked');
      if (noDealMsg)  noDealMsg.classList.add('is-hidden');
      if (dealHint)   dealHint.classList.add('is-hidden');

      // Populate and show price summary
      var cfg = dealConfig[deal];
      if (cfg && priceSummary) {
        if (priceNormalVal)  priceNormalVal.textContent  = cfg.normal;
        if (priceClubVal)    priceClubVal.textContent    = cfg.club;
        if (priceSavingsVal) priceSavingsVal.textContent = cfg.savings;
        priceSummary.removeAttribute('hidden');
      }

      // Smooth scroll to flavor panel on mobile
      if (window.innerWidth < 740 && flavorsPanel) {
        setTimeout(function () {
          flavorsPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }

      updateUI();
    }

    dealCards.forEach(function (card) {
      card.addEventListener('click', function () { selectDeal(card); });
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          selectDeal(card);
        }
      });
    });

    // ── Quantity buttons ───────────────────────────────────────────────
    flavorCards.forEach(function (card) {
      var vid   = card.dataset.variant;
      var minus = card.querySelector('.abz-qty-minus');
      var plus  = card.querySelector('.abz-qty-plus');

      minus.addEventListener('click', function () {
        if (quantities[vid] > 0) {
          quantities[vid]--;
          updateUI();
        }
      });

      plus.addEventListener('click', function () {
        if (getTotalQty() < requiredQty) {
          quantities[vid]++;
          var nameEl = card.querySelector('.abz-flavor-card__name');
          trackEvent('club_flavor_added', {
            deal:     selectedDeal,
            flavor:   nameEl ? nameEl.textContent.trim() : vid,
            quantity: quantities[vid]
          });
          updateUI();
        }
      });
    });

    // ── Add to cart ────────────────────────────────────────────────────
    atcBtn.addEventListener('click', function () {
      if (atcBtn.disabled || atcBtn.classList.contains('is-loading')) return;
      addToCart();
    });

    // ── Helpers ────────────────────────────────────────────────────────
    function getTotalQty() {
      return Object.values(quantities).reduce(function (sum, q) { return sum + q; }, 0);
    }

    function getCompleteBtnText() {
      var price = dealConfig[selectedDeal] ? dealConfig[selectedDeal].club : '';
      return 'Voeg bundel toe voor ' + price + ' \u2192';
    }

    function updateUI() {
      var total     = getTotalQty();
      var remaining = requiredQty - total;
      var pct       = requiredQty > 0 ? Math.min(100, Math.round((total / requiredQty) * 100)) : 0;
      var complete  = total === requiredQty && requiredQty > 0;

      // Progress bar
      if (progressBar)    progressBar.style.width = pct + '%';
      if (progressBarWrap) progressBarWrap.setAttribute('aria-valuenow', pct);

      // Progress text
      if (progressText) {
        if (complete) {
          var savings = dealConfig[selectedDeal] ? dealConfig[selectedDeal].savings : '';
          progressText.textContent = '\uD83C\uDF89 Bundel compleet! Je bespaart ' + savings;
          progressText.classList.add('is-complete');
        } else if (selectedDeal) {
          progressText.textContent = 'Nog ' + remaining + ' pack' + (remaining === 1 ? '' : 's') + ' kiezen';
          progressText.classList.remove('is-complete');
        }
      }

      // Fire club_bundle_completed exactly once per completion
      if (complete && !bundleCompleteTracked) {
        bundleCompleteTracked = true;
        trackEvent('club_bundle_completed', {
          deal:             selectedDeal,
          selected_flavors: getSelectedFlavors(),
          bundle_price:     dealConfig[selectedDeal] ? dealConfig[selectedDeal].club : ''
        });
      } else if (!complete) {
        bundleCompleteTracked = false;
      }

      // Per-card: quantity display + button states + selection highlight
      flavorCards.forEach(function (card) {
        var vid   = card.dataset.variant;
        var qty   = quantities[vid] || 0;
        var minus = card.querySelector('.abz-qty-minus');
        var plus  = card.querySelector('.abz-qty-plus');
        var qtyEl = card.querySelector('.abz-qty-num');

        if (qtyEl)  qtyEl.textContent = qty;
        if (minus)  minus.disabled = qty === 0;
        if (plus)   plus.disabled  = total >= requiredQty;

        if (qty > 0) {
          card.classList.add('is-selected');
        } else {
          card.classList.remove('is-selected');
        }
      });

      // ATC button copy and state
      if (complete) {
        atcBtn.disabled = false;
        atcBtn.removeAttribute('aria-disabled');
        atcBtn.textContent = getCompleteBtnText();
      } else if (selectedDeal) {
        atcBtn.disabled = true;
        atcBtn.setAttribute('aria-disabled', 'true');
        atcBtn.textContent = 'Kies nog ' + remaining + ' pack' + (remaining === 1 ? '' : 's');
      } else {
        atcBtn.disabled = true;
        atcBtn.setAttribute('aria-disabled', 'true');
        atcBtn.textContent = 'Kies eerst een deal';
      }
    }

    function addToCart() {
      var items = [];

      flavorCards.forEach(function (card) {
        var vid = card.dataset.variant;
        var qty = quantities[vid] || 0;
        if (qty > 0) {
          items.push({
            id: parseInt(vid, 10),
            quantity: qty,
            properties: {
              _bundle_source: 'spicebombs_club',
              _bundle_deal: selectedDeal
            }
          });
        }
      });

      if (items.length === 0) return;

      // Loading state
      atcBtn.classList.add('is-loading');
      atcBtn.disabled = true;
      atcBtn.textContent = 'Toevoegen\u2026';

      // Let theme components (CartDrawer, CartNotificationDrawer) register
      // the section IDs they need bundled in the /cart/add.js response.
      var sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      var cartType = (window.themeVariables && window.themeVariables.settings)
        ? window.themeVariables.settings.cartType
        : 'drawer';

      var body = { items: items };
      if (sectionsToBundle.length > 0) {
        body.sections     = sectionsToBundle.join(',');
        body.sections_url = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart';
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().then(function (err) {
              throw new Error(err.description || 'Er is een fout opgetreden bij het toevoegen.');
            });
          }
          return res.json();
        })
        .then(function (responseJson) {
          atcBtn.classList.remove('is-loading');
          atcBtn.disabled = false;
          atcBtn.textContent = getCompleteBtnText();

          var _trackDeal     = selectedDeal;
          var _trackFlavors  = getSelectedFlavors();
          var _trackPrice    = dealConfig[selectedDeal] ? dealConfig[selectedDeal].club : '';

          trackEvent('club_bundle_added_to_cart', {
            deal:             _trackDeal,
            selected_flavors: _trackFlavors,
            bundle_price:     _trackPrice
          });

          sendClubWebhook(_trackDeal, _trackFlavors, _trackPrice);

          // If the theme is configured to use the cart page, redirect there.
          if (cartType === 'page') {
            window.location.href = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart';
            return;
          }

          // Otherwise open the cart drawer/popover exactly as the theme's own
          // ProductForm does: fetch current cart state, merge bundled sections,
          // then dispatch the two events the CartDrawer listens for.
          return fetch('/cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cartContent) {
              if (responseJson.sections) {
                cartContent['sections'] = responseJson.sections;
              }

              var addedItems = responseJson.hasOwnProperty('items')
                ? responseJson['items']
                : [responseJson];

              document.documentElement.dispatchEvent(new CustomEvent('variant:add', {
                bubbles: true,
                detail: { items: addedItems, cart: cartContent }
              }));

              document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
                bubbles: true,
                detail: { baseEvent: 'variant:add', cart: cartContent }
              }));
            });
        })
        .catch(function (err) {
          console.error('[Spicebombs Club Builder] Add-to-cart error:', err);
          atcBtn.classList.remove('is-loading');
          atcBtn.disabled = false;
          atcBtn.textContent = getCompleteBtnText();
          alert(err.message || 'Er is iets misgegaan. Probeer het opnieuw.');
        });
    }
  });
})();
